/**
 * useOverlayPortalScope — jawne „to jest portal TEGO okna" dla komponentów z uchwytami dnd-kit.
 *
 * Popover (Radix) renderuje się w portalu `<body>`, czyli POZA panelem okna, choć w drzewie React
 * jest jego dzieckiem. Okno (`Overlay`) decyduje, czy Esc należy do trwającego przeciągania
 * dnd-kit (wtedy go przepuszcza), po MIEJSCU AKTYWNEGO UCHWYTU: w panelu okna albo w portalu,
 * który się mu zarejestrował. Portal, którego nikt nie zarejestruje, jest dla okna „obcy" —
 * przeciąganie w nim nie odbiera oknu Esc (i odwrotnie: przeciąganie gdziekolwiek indziej
 * w dokumencie nie przepuszcza Esc z takiego portalu).
 *
 * Użycie: `ref={useOverlayPortalScopeRef()}` na korzeniu portalowanej treści z uchwytami
 * (np. `PopoverPrimitive.Content`). Poza `Overlay` ref jest bez efektu. Okno zagnieżdżone
 * przekazuje rejestrację także oknu-rodzicowi (Esc bąbelkuje do niego przez drzewo React).
 */

import { createContext, type RefCallback, useCallback, useContext, useRef } from 'react';

/** Rejestruje element jako portal okna; zwraca funkcję wyrejestrowującą. */
export type RegisterOverlayPortal = (element: HTMLElement) => () => void;

/** Dostarczany przez `Overlay`; `null` poza oknem. */
export const OverlayPortalScopeContext = createContext<RegisterOverlayPortal | null>(null);

/**
 * Zwraca stabilny ref-callback rejestrujący element w najbliższym `Overlay` (no-op bez niego).
 * Wyrejestrowuje przy `null` (odmontowanie) i przy zmianie elementu; zwraca też funkcję
 * sprzątającą, którą React 19 woła zamiast przekazywania `null`.
 */
export function useOverlayPortalScopeRef<T extends HTMLElement = HTMLElement>(): RefCallback<T> {
  const register = useContext(OverlayPortalScopeContext);
  const unregisterRef = useRef<(() => void) | null>(null);

  return useCallback(
    (element: T | null) => {
      const release = (): void => {
        unregisterRef.current?.();
        unregisterRef.current = null;
      };
      release();
      if (element === null || register === null) return undefined;
      unregisterRef.current = register(element);
      return release;
    },
    [register],
  );
}
